/**
 * The town's camera, as the daily round (v1 ticket 15) moves it: where a lot sits on screen, the view that frames
 * some lots inside a part of the screen, a flight between two views, and where a card hangs under a lot as a sign.
 * DOM-free, so the node tests run it; both renderers (D and B) share one projection (layout.ts `sx`, `sy`).
 *
 * A view is the renderers' own camera: a world point (x, y) shows at (x·scale + tx, y·scale + ty) CSS px.
 */
import { HH, HW, sx, sy } from './layout';

export interface View { scale: number; tx: number; ty: number }
export interface Rect { x: number; y: number; w: number; h: number }
export type Cell = [number, number];

export const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
/** Ease in and out (cubic). */
export const ease = (k: number) => (k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2);

/** Lot coordinates (x, y, and z up in world px) → CSS px on screen. */
export function project(v: View, x: number, y: number, z = 0): [number, number] {
  return [sx(x, y) * v.scale + v.tx, sy(x, y, z) * v.scale + v.ty];
}

/** The view that fits `cells` (and `top` world px of height above them) into `area`, never closer than `max`. */
export function frameCells(cells: readonly Cell[], area: Rect, top = 26, max = 6): View {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const [gx, gy] of cells) {
    for (const [x, y] of [[gx, gy], [gx + 1, gy], [gx, gy + 1], [gx + 1, gy + 1]]) {
      x0 = Math.min(x0, sx(x, y)); x1 = Math.max(x1, sx(x, y));
      y0 = Math.min(y0, sy(x, y, top)); y1 = Math.max(y1, sy(x, y, 0));
    }
  }
  if (!Number.isFinite(x0)) return { scale: 1, tx: area.x + area.w / 2, ty: area.y + area.h / 2 };
  const s = Math.min(area.w / (x1 - x0), area.h / (y1 - y0), max);
  return { scale: s, tx: area.x + area.w / 2 - ((x0 + x1) / 2) * s, ty: area.y + area.h / 2 - ((y0 + y1) / 2) * s };
}

/** Screen rect around cell (gx, gy) and `top` world px above it, padded by `pad` lot widths. */
export function cellBox(v: View, gx: number, gy: number, top = 30, pad = 1.3): Rect {
  const s = v.scale, [cx, cy] = project(v, gx + 0.5, gy + 0.5), h = top + 8;
  return { x: cx - HW * s * pad, y: cy - (h + HH * pad) * s, w: 2 * HW * s * pad, h: (h + 2 * HH * pad) * s };
}

/** The smallest rect holding every rect; null for none. */
export function unionRect(rs: readonly Rect[]): Rect | null {
  if (!rs.length) return null;
  const x = Math.min(...rs.map((r) => r.x)), y = Math.min(...rs.map((r) => r.y));
  return { x, y, w: Math.max(...rs.map((r) => r.x + r.w)) - x, h: Math.max(...rs.map((r) => r.y + r.h)) - y };
}

/** A rect cut to the screen (a grab outside the canvas would read nothing); at least 1 px each way. */
export function clipRect(r: Rect, w: number, h: number): Rect {
  const x = Math.max(0, Math.min(w - 1, r.x)), y = Math.max(0, Math.min(h - 1, r.y));
  return { x, y, w: Math.max(1, Math.min(w, r.x + r.w) - x), h: Math.max(1, Math.min(h, r.y + r.h) - y) };
}

/**
 * The view `e` (0..1, eased) of the way from `a` to `b` on a w × h screen: the zoom moves in log space, so it
 * feels even, and the world point at the screen's centre moves in a straight line.
 */
export function flightAt(a: View, b: View, e: number, w: number, h: number): View {
  const ca = [(w / 2 - a.tx) / a.scale, (h / 2 - a.ty) / a.scale], cb = [(w / 2 - b.tx) / b.scale, (h / 2 - b.ty) / b.scale];
  const s = Math.exp(Math.log(a.scale) * (1 - e) + Math.log(b.scale) * e);
  const cx = ca[0] + (cb[0] - ca[0]) * e, cy = ca[1] + (cb[1] - ca[1]) * e;
  return { scale: s, tx: w / 2 - cx * s, ty: h / 2 - cy * s };
}

/** Height of the round's bar across the top of the screen (CSS px). */
export const BAR = 56;

/** The whole screen under the bar, with room at the foot for the round's buttons. */
export const wholeArea = (w: number, h: number): Rect => ({ x: 12, y: BAR + 8, w: Math.max(40, w - 24), h: Math.max(80, h - BAR - 96) });

/** The strip at the top where a first look's lot is framed; its card hangs below it. */
export const lotArea = (w: number, h: number): Rect => ({ x: 20, y: BAR + 8, w: Math.max(40, w - 40), h: Math.max(140, h * 0.28) });

/** The strip at the top where a district that has just landed is framed (v1 ticket 41); its sign hangs below it. */
export const districtArea = (w: number, h: number): Rect => ({ x: 20, y: BAR + 8, w: Math.max(40, w - 40), h: Math.max(160, h * 0.36) });

/**
 * The room over the end's sheet, where the lots that changed are framed with their chips: under the bar, with room
 * above the tallest building for a chip, down to `bottom` (the sheet's top, CSS px).
 */
export const endArea = (w: number, bottom: number): Rect =>
  ({ x: 20, y: BAR + 28, w: Math.max(40, w - 40), h: Math.max(80, bottom - BAR - 40) });

/** Where a sign hangs: its left edge and top (CSS px), and its pointer's x inside it, under screen point (x, y). */
export interface SignSpot { left: number; top: number; width: number; px: number }

/** A sign `width` px wide under the lot's front corner (x, y), kept on a w-px-wide screen. */
export function signSpot(x: number, y: number, w: number, maxWidth = 420): SignSpot {
  const width = Math.max(40, Math.min(maxWidth, w - 24));
  const left = Math.max(12, Math.min(w - width - 12, x - width / 2));
  return { left, top: Math.round(y + 14), width, px: Math.round(Math.max(18, Math.min(width - 18, x - left))) };
}
